import { useMutation } from '@apollo/client';
import { Check as CheckIcon, Close as CloseIcon } from '@mui/icons-material';
import {
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  FormControl,
  TextField,
  Typography,
} from '@mui/material';
import { amber, green } from '@mui/material/colors';
import * as Schema from 'generated/graphql/schema';
import { FunctionComponent, useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

import TextContentArea from '@/components/containers/text-content-area';
import AndonCallDialogHeader from '@/components/dialogs/andon/header';
import { Context } from '@/contexts/company';
import { andonCallRelease, andonCallResolve } from '@/graphql/mutations';
import { buildSummary } from '@/helpers/andon';
import { guardedCallback } from '@/helpers/helper-functions';
import { useUtils } from '@/hocs';

const useStyles = makeStyles()((theme) => ({
  iconSizeLeft: {
    color: 'inherit',
    height: '1em !important',
    width: '1em !important',
    fontSize: theme.typography.pxToRem(24),
    marginRight: theme.spacing(),
  },
  iconSizeRight: {
    color: 'inherit',
    height: '1em !important',
    width: '1em !important',
    fontSize: theme.typography.pxToRem(24),
    marginLeft: theme.spacing(),
  },
  buttonWithIconRight: {
    '& > span > svg': {
      marginLeft: theme.spacing(),
    },
  },
  buttonWithIconLeft: {
    '& > span > svg': {
      marginRight: theme.spacing(),
    },
  },
  hideWhileSmall: {
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  buttonNegative: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.getContrastText(theme.palette.error.main),
    '&:hover': {
      backgroundColor: theme.palette.error.main,
    },
  },
  buttonNeutral: {
    backgroundColor: amber[600],
    color: theme.palette.getContrastText(amber[600]),
    '&:hover': {
      backgroundColor: amber[600],
    },
  },
  buttonPositive: {
    backgroundColor: green[600],
    color: theme.palette.getContrastText(green[600]),
    '&:hover': {
      backgroundColor: green[600],
    },
  },
}));

export interface Properties {
  call: Schema.AndonListCallsQuery['andon'][0]['calls'][0];
  onClose(..._: unknown[]): void;
}

const AndonResolveCallDialog: FunctionComponent<Properties> = ({ call, onClose }) => {
  const [t] = useTranslation();
  const { classes, cx } = useStyles();
  const utils = useUtils();

  const baseState = {
    solution: '',
    initials: '',
    submitAttempt: false,
  };

  const [solution, setSolution] = useState(baseState.solution);
  const [initials, setInitials] = useState(baseState.initials);
  const [submitAttempt, setSubmitAttempt] = useState(baseState.submitAttempt);

  const onSolutionChange = useCallback(
    guardedCallback((event) => {
      setSubmitAttempt(baseState.submitAttempt);
      return setSolution(event);
    }),
    [],
  );
  const onInitialsChange = useCallback(
    guardedCallback((event) => {
      setSubmitAttempt(baseState.submitAttempt);
      return setInitials(event);
    }),
    [],
  );

  const onExited = useCallback(() => {
    const states: [Function, string][] = [
      [setSolution, baseState.solution],
      [setInitials, baseState.initials],
    ];

    states.forEach(([setter, state]) => setter(() => state));
  }, []);

  const [resolve, { loading: resolving }] = useMutation<
    Schema.AndonCallResolveMutation,
    Schema.AndonCallResolveMutationVariables
  >(andonCallResolve);
  const [release, { loading: releasing }] = useMutation<
    Schema.AndonCallReleaseMutation,
    Schema.AndonCallReleaseMutationVariables
  >(andonCallRelease);

  const { companyId } = useContext(Context);

  const callAction = call.action.take;
  if (!callAction || !companyId) {
    return null;
  }

  const solutionLabel = t(['shared:solution'], { defaultValue: 'Solution' });

  const textFields = [
    {
      id: 'solution',
      label: solutionLabel,
      rows: 4,
      value: solution,
      onChange: onSolutionChange,
      required: true,
      multiline: true,
      error: !solution.length,
    },
    {
      id: 'initials',
      label: t(['shared:initials'], { defaultValue: 'Initials' }),
      rows: 1,
      value: initials,
      onChange: onInitialsChange,
      required: true,
      error: !initials.length,
    },
  ];

  const disableButton = resolving || textFields.some((field) => field.required && field.error);

  const summary = buildSummary(call.action, utils);

  return (
    <AndonCallDialogHeader onClose={onClose} onExited={onExited}>
      <DialogContent>
        {callAction.author ? (
          <TextContentArea>
            <Typography variant="subtitle2">{t(['andon:call:takenBy'], { defaultValue: 'Taken by' })}</Typography>
            <Typography>{callAction.author}</Typography>
          </TextContentArea>
        ) : null}
        <TextContentArea>
          <Typography variant="subtitle2">{t(['shared:summary'], { defaultValue: 'Summary' })}</Typography>
          <Typography>{summary}</Typography>
        </TextContentArea>
        {textFields.map(({ id, label, rows, value, onChange, required, multiline, error }) => {
          return (
            <TextContentArea key={id}>
              <FormControl variant="filled" fullWidth>
                <TextField
                  id={id}
                  label={label}
                  rows={rows}
                  autoFocus
                  variant="outlined"
                  multiline={multiline}
                  onChange={onChange}
                  value={value}
                  required={required}
                  error={submitAttempt && error}
                />
              </FormControl>
            </TextContentArea>
          );
        })}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" className={classes.hideWhileSmall}>
          {t(['shared:close'], { defaultValue: 'Close' })}
        </Button>
        <Button
          onClick={async () => {
            return release({
              variables: {
                companyId,
                id: call.id,
              },
            });
          }}
          color="primary"
          variant="contained"
          disabled={releasing}
          className={cx(classes.buttonWithIconLeft, classes.buttonNegative)}
        >
          {releasing ? <CircularProgress className={cx(classes.iconSizeLeft)} /> : <CloseIcon />}
          {t(['shared:release'], { defaultValue: 'Release' })}
        </Button>
        <Button
          onClick={async () =>
            resolve({
              variables: {
                companyId,
                id: call.id,
                author: initials,
                details: solution,
              },
            })
          }
          disabled={disableButton}
          color="primary"
          variant="contained"
          className={cx(classes.buttonWithIconLeft, classes.buttonPositive)}
        >
          {resolving ? <CircularProgress className={classes.iconSizeLeft} /> : <CheckIcon />}
          {t(['shared:resolve'], { defaultValue: 'Resolve' })}
        </Button>
      </DialogActions>
    </AndonCallDialogHeader>
  );
};

export default AndonResolveCallDialog;
